import React, { createContext, useContext, useState } from "react";
import { DiscoveryRequestTask } from "../models/DiscoveryRequestTask";
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "../design/alert";
import { Button } from "../design/button";
import { useDiscoveryRequestTask } from "../contexts/DiscoveryRequestTaskContext";

interface Context {
  openAlert: (discoveryRequestTask: DiscoveryRequestTask) => void;
  closeAlert: () => void;
  isOpen: boolean;
  discoveryRequestTask: DiscoveryRequestTask | undefined;
}

const CompleteDiscoveryRequestTaskAlertProviderContext = createContext<
  Context | undefined
>(undefined);

export const CompleteDiscoveryRequestTaskAlertProviderProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { updateDiscoveryRequestTask } = useDiscoveryRequestTask();
  const [discoveryRequestTask, setDiscoveryRequestTask] = useState<
    DiscoveryRequestTask | undefined
  >(undefined);
  const [isOpen, setIsOpen] = useState(false);

  const openAlert = (discoveryRequestTask: DiscoveryRequestTask) => {
    setDiscoveryRequestTask(discoveryRequestTask);
    setIsOpen(true);
  };

  const closeAlert = () => {
    setIsOpen(false);
    setDiscoveryRequestTask(undefined);
  };

  const handleYes = async () => {
    if (!discoveryRequestTask) return;
    try {
      await updateDiscoveryRequestTask(discoveryRequestTask._id, {
        isCompleted: true,
        completedAt: new Date(),
      });
      closeAlert();
    } catch (error) {
      console.error("Error completing discoveryRequestTask:", error);
      alert("Failed to complete discoveryRequestTask. Please try again later.");
    }
  };

  return (
    <CompleteDiscoveryRequestTaskAlertProviderContext.Provider
      value={{ openAlert, closeAlert, isOpen, discoveryRequestTask }}
    >
      {discoveryRequestTask && (
        <Alert open={isOpen} onClose={closeAlert}>
          <AlertTitle>Mark Complete?</AlertTitle>
          <AlertDescription>Are you sure</AlertDescription>
          <AlertActions>
            <Button plain onClick={closeAlert}>
              Cancel
            </Button>
            <Button color={"green"} onClick={handleYes}>
              Yes
            </Button>
          </AlertActions>
        </Alert>
      )}
      {children}
    </CompleteDiscoveryRequestTaskAlertProviderContext.Provider>
  );
};

export const useCompleteDiscoveryRequestTaskAlert = () => {
  const ctx = useContext(CompleteDiscoveryRequestTaskAlertProviderContext);
  if (!ctx)
    throw new Error(
      "useCompleteDiscoveryRequestTaskAlert must be used inside CompleteDiscoveryRequestTaskAlertProviderProvider",
    );
  return ctx;
};
